import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@client/lib/utils';
import { hapticLight, hapticMedium } from '@client/lib/haptics';
import { Plus } from 'lucide-react';

// Quick reaction emojis
const QUICK_EMOJIS = ['❤️', '😍', '🔥', '😂', '🎉', '👏'] as const;

// Full emoji categories for the expanded picker
const EMOJI_CATEGORIES = {
  'Smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐'],
  'Love': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '😻', '💑', '👩‍❤️‍👨', '👨‍❤️‍👨', '👩‍❤️‍👩', '💏', '👩‍❤️‍💋‍👨', '👨‍❤️‍💋‍👨', '👩‍❤️‍💋‍👩'],
  'Gestures': ['👍', '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '💪', '🦾', '🙏'],
  'Celebration': ['🎉', '🎊', '🎈', '🎁', '🎀', '🎂', '🍰', '🧁', '🥂', '🍾', '🥳', '🎆', '🎇', '✨', '💫', '🌟', '⭐', '🏆', '🥇', '🎖️', '🏅', '🎗️', '🎄', '🎃', '🎭', '🎪'],
  'Nature': ['🌸', '💐', '🌷', '🌹', '🥀', '🌺', '🌻', '🌼', '🌿', '☘️', '🍀', '🌱', '🌲', '🌳', '🌴', '🌵', '🌾', '🌈', '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️', '❄️', '☃️', '⛄', '🔥', '💧', '🌊'],
  'Animals': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🐇', '🦝', '🦨', '🦡', '🦦', '🦥', '🐁', '🐀', '🐿️', '🦔'],
  'Food': ['🍕', '🍔', '🍟', '🌭', '🍿', '🧂', '🥓', '🥚', '🍳', '🧇', '🥞', '🧈', '🍞', '🥐', '🥖', '🥨', '🧀', '🥗', '🥙', '🥪', '🌮', '🌯', '🫔', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍩', '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '☕', '🫖', '🍵', '🧃', '🥤', '🧋', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾', '🧊'],
};

interface EmojiPickerProps {
  selectedEmojis: string[];
  onSelect: (emoji: string) => void;
  onClose: () => void;
  position?: { x: number; y: number };
}

export function EmojiPicker({ selectedEmojis, onSelect, onClose, position }: EmojiPickerProps) {
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [activeCategory, setActiveCategory] = useState<keyof typeof EMOJI_CATEGORIES>('Smileys');
  const containerRef = useRef<HTMLDivElement>(null);
  const [animateIn, setAnimateIn] = useState(false);

  // Animate in on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      setAnimateIn(true);
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleQuickSelect = useCallback((emoji: string) => {
    hapticMedium();
    onSelect(emoji);
  }, [onSelect]);

  const handleFullSelect = useCallback((emoji: string) => {
    hapticLight();
    onSelect(emoji);
    setShowFullPicker(false);
  }, [onSelect]);

  const toggleFullPicker = useCallback(() => {
    hapticLight();
    setShowFullPicker(prev => !prev);
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'absolute z-50 transition-all duration-300 ease-out',
        animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      )}
      style={{
        left: position?.x ?? '50%',
        bottom: position?.y ?? 60,
        transform: position ? undefined : 'translateX(-50%)',
      }}
    >
      {/* Quick emoji bar */}
      <div className="flex items-center gap-1 bg-black/80 dark:bg-white/20 backdrop-blur-xl rounded-full px-2 py-1.5 shadow-2xl border border-white/10">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => handleQuickSelect(emoji)}
            className={cn(
              'w-10 h-10 flex items-center justify-center rounded-full text-2xl transition-all duration-200',
              'hover:scale-125 active:scale-95',
              selectedEmojis.includes(emoji) 
                ? 'bg-white/30 scale-110 ring-2 ring-white/50' 
                : 'hover:bg-white/10'
            )}
          >
            <span className="drop-shadow-sm">{emoji}</span>
          </button>
        ))}
        
        {/* Divider */}
        <div className="w-px h-6 bg-white/20 mx-1" />
        
        {/* More button */}
        <button
          onClick={toggleFullPicker}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200',
            'hover:bg-white/20 active:scale-95',
            showFullPicker && 'bg-white/20 rotate-45'
          )}
        >
          <Plus className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Full emoji picker */}
      {showFullPicker && (
        <div 
          className={cn(
            'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-80 max-h-72',
            'bg-black/90 dark:bg-zinc-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10',
            'overflow-hidden transition-all duration-300',
            'animate-slide-up'
          )}
        >
          {/* Category tabs */}
          <div className="flex gap-1 p-2 border-b border-white/10 overflow-x-auto scrollbar-hide">
            {(Object.keys(EMOJI_CATEGORIES) as Array<keyof typeof EMOJI_CATEGORIES>).map((category) => (
              <button
                key={category}
                onClick={() => {
                  hapticLight();
                  setActiveCategory(category);
                }}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all',
                  activeCategory === category
                    ? 'bg-white/20 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                )}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Emoji grid */}
          <div className="p-2 h-48 overflow-y-auto">
            <div className="grid grid-cols-8 gap-1">
              {EMOJI_CATEGORIES[activeCategory].map((emoji, index) => (
                <button
                  key={`${emoji}-${index}`}
                  onClick={() => handleFullSelect(emoji)}
                  className={cn(
                    'w-9 h-9 flex items-center justify-center rounded-lg text-xl',
                    'transition-all duration-150 hover:scale-125 hover:bg-white/10 active:scale-95',
                    selectedEmojis.includes(emoji) && 'bg-white/20 ring-1 ring-white/30'
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Floating emoji animation component
interface FloatingEmojiProps {
  emoji: string;
  onComplete: () => void;
}

export function FloatingEmoji({ emoji, onComplete }: FloatingEmojiProps) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 1000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
      <span 
        className="text-8xl drop-shadow-2xl animate-emoji-pop"
        style={{ 
          filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.5))'
        }}
      >
        {emoji}
      </span>
    </div>
  );
}

// Reaction display component
interface ReactionDisplayProps {
  reactions: { emoji: string; count: number }[];
  onReactionClick?: (emoji: string) => void;
  currentUserReactions?: string[];
  compact?: boolean;
}

export function ReactionDisplay({ reactions, onReactionClick, currentUserReactions = [], compact }: ReactionDisplayProps) {
  if (reactions.length === 0) return null;

  // Sort by count descending
  const sortedReactions = [...reactions].sort((a, b) => b.count - a.count);
  
  // In compact mode, show only top 3
  const displayReactions = compact ? sortedReactions.slice(0, 3) : sortedReactions;
  const totalCount = reactions.reduce((sum, r) => sum + r.count, 0);

  if (compact) {
    // Compact: just show emoji stack with count
    return (
      <div className="flex items-center gap-1">
        <div className="flex -space-x-1">
          {displayReactions.map((r, i) => (
            <span 
              key={r.emoji}
              className="text-base"
              style={{ zIndex: displayReactions.length - i }}
            >
              {r.emoji}
            </span>
          ))}
        </div>
        <span className="text-sm font-medium text-muted-foreground">
          {totalCount}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {displayReactions.map((reaction) => (
        <button
          key={reaction.emoji}
          onClick={() => onReactionClick?.(reaction.emoji)}
          className={cn(
            'flex items-center gap-1 px-2 py-0.5 rounded-full text-sm transition-all',
            'bg-secondary/50 hover:bg-secondary',
            currentUserReactions.includes(reaction.emoji) && 'bg-primary/20 ring-1 ring-primary/50'
          )}
        >
          <span>{reaction.emoji}</span>
          <span className="font-medium text-muted-foreground">{reaction.count}</span>
        </button>
      ))}
    </div>
  );
}
